<template>
  <IndicatorCard
    :loading="loading"
    :has-data="adhesionCalleds.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum dado encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row class="ma-0" justify="center">
      <v-col cols="12" sm="5">
        <strong>Total: {{ total }}</strong>
        <BaseEChart :option="barOption" :loading="loading" height="280px" />
      </v-col>

      <v-col cols="12" sm="5">
        <BaseEChart :option="gaugeOption" :loading="loading" height="280px" />
      </v-col>

      <v-col cols="12">
        <v-data-table
          :loading="loading"
          :headers="header"
          :items="adhesionCalleds"
          class="main-table my-2"
          :hide-default-footer="!filterProp.data"
          hide-default-header
          :header-props="headerProps"
          :items-per-page="7"
          :mobile-breakpoint="0"
        >
          <template #header="{ props, on }">
            <thead>
              <tr>
                <th
                  class="text-center"
                  v-for="header in props.headers"
                  :key="header.value"
                >
                  <v-simple-checkbox
                    v-if="header.value === 'data-table-select'"
                    v-model="props.everyItem"
                    :indeterminate="props.someItems && !props.everyItem"
                    color="purple"
                    @input="on['toggle-select-all']"
                  ></v-simple-checkbox>

                  <div>
                    <v-hover v-slot="{ hover }">
                      <div>
                        <span>{{ $t(header.text) }}</span>
                        <v-icon
                          v-if="
                            hover &&
                            header.value != 'data-table-select' &&
                            header.value != 'actions'
                          "
                          color="white"
                          class="mx-2"
                          @click="on.sort(header.value)"
                        >
                          {{
                            props.options.sortBy.includes(header.value)
                              ? `mdi-chevron-down`
                              : `mdi-chevron-up`
                          }}
                        </v-icon>
                      </div>
                    </v-hover>
                  </div>
                </th>
              </tr>
            </thead>
          </template>

          <template v-slot:[`item.calleds`]="{ item }">
            {{ item.calleds }}
          </template>

          <template v-slot:[`item.others`]="{ item }">
            {{ item.others }}
          </template>

          <template v-slot:[`item.percent`]="{ item }">
            {{ item.percent }} %
          </template>
        </v-data-table>
        <div class="pagination" v-if="!filterProp.data">
          <div class="item-pagination block-two">
            <v-pagination
              class="pagination-item"
              v-model="page"
              :length="totalPages"
              :total-visible="9"
            ></v-pagination>
          </div>
        </div>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="
              $t('Digite sua análise crítica para o indicador abaixo')
            "
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          ></tiptap-vuetify>
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import BaseEChart from '../../shared/BaseEChart.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import { buildHorizontalBarOption, buildGaugeOption } from '../../shared/chartConfigs'
import moment from 'moment'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  components: {
    IndicatorCard,
    BaseEChart,
    DateRangePicker,
    TiptapVuetify,
  },

  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    const lastMonthStart = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')
    const lastMonthEnd = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    this.startDate = lastMonthStart
    this.endDate = lastMonthEnd

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  data() {
    return {
      excelLoading: false,
      extensions: [
        History, Blockquote, Link, Underline, Strike, Italic, ListItem,
        BulletList, OrderedList,
        [Heading, { options: { levels: [1, 2, 3] } }],
        Bold, Code, HorizontalRule, Paragraph, HardBreak,
      ],
      filter: {
        page: 1,
        pageSize: 7,
        institutionId: null,
        unidadeIds: [],
        startDate: null,
        endDate: null,
        review: '',
      },
      apiService: new ApiService(),
      loading: true,
      adhesionCalleds: [],
      page: 1,
      totalPages: 1,
      total: 0,
      percent: 0,
      calleds: 0,
      calledsOthers: 0,
      startDate: '',
      endDate: '',
      headerProps: { sortByText: 'Ordenar por' },
      header: [
        { text: '', align: 'center', value: '' },
        { text: 'Corretivas Abertas Por Módulo de Chamados', align: 'center', value: 'calleds' },
        { text: 'Corretivas Abertas Por Outros Canais', align: 'center', value: 'others' },
        { text: '% O.S. Corretivas Abertas Via Chamados', align: 'center', value: 'percent' },
      ],
    }
  },

  computed: {
    indicator() {
      return getIndicatorById('adhesion_called_module') || {}
    },
    barOption() {
      return buildHorizontalBarOption({
        categories: [''],
        series: [
          {
            name: 'Via Chamados',
            data: [this.calleds],
            color: '#87AFA6',
          },
          {
            name: 'Outros Canais',
            data: [this.calledsOthers],
            color: '#BAB3D3',
          },
        ],
        showLegend: true,
      })
    },

    gaugeOption() {
      return buildGaugeOption({
        value: this.percent,
        label: 'Adesão',
      })
    },
  },

  methods: {
    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')
      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filterProp.data = null
      this.list()
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('adhesion_called_module').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    extractChartData(response) {
      this.adhesionCalleds = []

      this.total =
        response.content.abertasChamado + response.content.abertasOutros

      this.calleds = response.content.abertasChamado
      this.calledsOthers = response.content.abertasOutros
      this.percent = response.content.porcentagem

      this.adhesionCalleds.push({
        calleds: response.content.abertasChamado,
        others: response.content.abertasOutros,
        percent: response.content.porcentagem,
      })
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          this.extractChartData(JSON.parse(this.filterProp.data))
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.pageSize = 7
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/adhesionCalled',
          this.filter
        )
        this.extractChartData(response)
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    page() {
      this.filter.page = this.page
      this.list()
    },
    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
